import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Frame, useEditor, Element } from "@craftjs/core";
import Scrollbars from "react-custom-scrollbars-2";
import { Monitor, Smartphone, Tablet } from "lucide-react";
import { Style } from "react-style-tag";
import Header from "../Header";
import Toolbox from "../Toolbox/Toolbox";
import { Box, ButtonProps, Stack, Tab, Tabs } from "@mui/material";
import Settings from "../Settings/Settings";
import { Template, Website } from "@/types";
import ContainerBlock from "@/components/editor/blocks/ContainerBlock";
import OrganizationThemeProvider from "@/providers/OrganizationThemeProvider";
import "./ViewPort.scss";

export interface Props {
  title: string;
  data: string;
  actions?: ButtonProps[];
  onSave: (data: string) => void;
  onClose: () => void;
  json: string;
  template: Template;
  website?: Website;
  styles: string;
}

const ViewPort = (props: Props) => {
  const { connectors } = useEditor();
  const navigate = useNavigate();
  const [viewPort, setViewPort] = useState("sm");

  const viewportWidth = useMemo(() => {
    if (props.template?.type === "menu") {
      return "600px !important";
    }

    switch (viewPort) {
      case "sm":
        return "500px !important";
      case "md":
        return "700px !important";
      default:
        return "100% !important";
    }
  }, [props.template?.type, viewPort]);

  const updateViewportUrl = (viewport: string) => {
    navigate(document.location.pathname + "?" + viewport);
  };

  const handleViewport = (_: string, value: string) => {
    const qs = new URLSearchParams(document.location.search);
    qs.set("viewport", value);
    updateViewportUrl(qs.toString());
  };

  useEffect(() => {
    const qs = new URLSearchParams(document.location.search);
    const vp = qs.get("viewport");

    if (vp) {
      const isStandardViewport = ["sm", "md", "lg"].includes(vp);

      if (isStandardViewport) {
        setViewPort(vp);
      } else {
        qs.set("viewport", "sm");
        updateViewportUrl(qs.toString());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document.location.search]);

  return (
    <div className="editor">
      <div className="editor__header">
        <Header {...props} />
      </div>
      <div className="editor__body">
        <Toolbox />
        <Scrollbars autoHide>
          <div className="editor__canvas" ref={(ref: any) => connectors.select(connectors.hover(ref, null), null)}>
            <Stack spacing={2} my={2} flex={1} alignItems="center">
              <Stack spacing={2} direction={"row"} justifyContent="center">
                <Tabs value={viewPort} onChange={handleViewport} indicatorColor="primary">
                  <Tab value="sm" icon={<Smartphone color={viewPort === "sm" ? "white" : "#ffffff7a"} />} />
                  <Tab value="md" icon={<Tablet color={viewPort === "md" ? "white" : "#ffffff7a"} />} />
                  <Tab value="lg" icon={<Monitor color={viewPort === "lg" ? "white" : "#ffffff7a"} />} />
                </Tabs>
              </Stack>

              <Box
                className="viewport"
                id="main-container"
                ref={(ref) => connectors.select(connectors.hover(ref, null), null)}
                sx={{ width: viewportWidth, transition: "width 0.25s" }}
              >
                <Style children={props.styles ?? props.website?.styles ?? props.template?.styles ?? ""} />
                <OrganizationThemeProvider>
                  <Frame data={props.json}>
                    <Element canvas is={ContainerBlock} custom={{ displayName: "App" }}></Element>
                  </Frame>
                </OrganizationThemeProvider>
              </Box>
            </Stack>
          </div>
        </Scrollbars>
        <Settings />
      </div>
    </div>
  );
};

export default ViewPort;
